import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAppContext } from "../../contexts/AppContext";

function ProfileUpdate() {
  const { backendUrl } = useAppContext();
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    password: "",
    confirmPassword: "",
    sex: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${backendUrl}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const user = response.data;
        if (user) {
          // Since /protected might only return token data, we should ideally have a /users/me or similar
          // but for now let's use what we have or assume the user exists.
          setFormData(prev => ({
            ...prev,
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            userName: user.userName || "",
            email: user.email || "",
            sex: user.sex || ""
          }));
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
  }, [backendUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      setMessage({ type: "danger", text: "Passwords do not match!" });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await axios.put(`${backendUrl}/users/profile`, formData);
      setMessage({ type: "success", text: response.data.message || "Profile updated successfully!" });
      // Optionally clear password fields
      setFormData(prev => ({ ...prev, password: "", confirmPassword: "" }));
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage({ 
        type: "danger", 
        text: error.response?.data?.message || "Failed to update profile. Please try again." 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-3 shadow-sm p-4 animate__animated animate__fadeIn">
      <h5 className="fw-bold mb-4">My Profile Settings</h5>
      
      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ type: "", text: "" })}></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">First Name</label>
          <input
            type="text"
            className="form-control"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="col-md-6">
          <label className="form-label fw-semibold">Last Name</label>
          <input
            type="text"
            className="form-control"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6">
          <label className="form-label fw-semibold">Username</label>
          <input
            type="text"
            className="form-control"
            name="userName"
            value={formData.userName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6">
          <label className="form-label fw-semibold">Email Address</label>
          <input
            type="email"
            className="form-control"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6">
          <label className="form-label fw-semibold">Sex</label>
          <select 
            className="form-select" 
            name="sex" 
            value={formData.sex} 
            onChange={handleChange}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>

        <div className="col-12 mt-4">
          <hr />
          <h6 className="fw-bold mb-3">Change Password (Leave blank to keep current)</h6>
        </div>

        <div className="col-md-6">
          <label className="form-label fw-semibold">New Password</label>
          <input
            type="password"
            className="form-control"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Min 6 characters"
          />
        </div>

        <div className="col-md-6">
          <label className="form-label fw-semibold">Confirm New Password</label>
          <input
            type="password"
            className="form-control"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 mt-4">
          <button 
            type="submit" 
            className="btn btn-primary px-5 py-2 fw-bold shadow-sm"
            disabled={loading}
            style={{ backgroundColor: "var(--primary-color)", border: "none" }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProfileUpdate;
